import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    Input,
    OnDestroy,
    Output,
} from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormControl,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
    Validator,
} from '@angular/forms';
import { ResourceService } from '@klickdata/core/resource';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, share, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
    selector: 'app-resource-article-code-form',
    templateUrl: './resource-article-code-form.component.html',
    styleUrls: ['./resource-article-code-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => ResourceArticleCodeFormComponent),
            multi: true,
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => ResourceArticleCodeFormComponent),
            multi: true,
        },
    ],
})
export class ResourceArticleCodeFormComponent implements AfterViewInit, ControlValueAccessor, Validator, OnDestroy {
    @Input() resourceId: number;
    @Input() placeholder: string = 'Please set course code';
    @Output() onValueUpdated: EventEmitter<string> = new EventEmitter<string>();
    public articleCodeControl = new FormControl('');
    public validArticleCode: number;
    public isDirty = false;
    private destroy$ = new Subject<boolean>();
    private propagateChange = (_: string) => {};
    private propagateTouched = () => {};
    private propagateValidationChange = () => {};
    private parentControl: AbstractControl;

    constructor(private resourceService: ResourceService, private cdRef: ChangeDetectorRef) {}

    ngAfterViewInit(): void {
        this.articleCodeControl.valueChanges
            .pipe(
                filter((query) => !!query),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((query) =>
                    this.resourceService.checkFieldValidaty({
                        article_code: query,
                        id: this.resourceId,
                    })
                ),
                map((res) => (res ? 1 : -1)),
                share(),
                tap((isValidCode) => {
                    if (isValidCode === -1) {
                        this.articleCodeControl.setErrors({ incorrect: true });
                        this.onValueUpdated.emit(null);
                    } else if (isValidCode === 1) {
                        this.onValueUpdated.emit(this.articleCodeControl.value);
                        const currentErrors = this.articleCodeControl.errors;
                        if (currentErrors && currentErrors.incorrect) {
                            const { incorrect, ...restErrors } = currentErrors;
                            const hasOtherErrors = Object.keys(restErrors).length > 0;
                            this.articleCodeControl.setErrors(hasOtherErrors ? restErrors : null);
                        }
                    }
                    this.propagateValidationChange();
                    this.cdRef.markForCheck();
                }),
                takeUntil(this.destroy$)
            )
            .subscribe((isValidCode) => {
                this.validArticleCode = isValidCode;
                this.cdRef.markForCheck();
            });

        this.articleCodeControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.isDirty = this.articleCodeControl.dirty;
            this.propagateChange(value);
            this.cdRef.markForCheck();
        });

        this.articleCodeControl.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
            this.cdRef.markForCheck();
        });
    }

    writeValue(value: string): void {
        if (value !== undefined && value !== null) {
            this.articleCodeControl.setValue(value, { emitEvent: false });
        } else {
            this.articleCodeControl.setValue('', { emitEvent: false });
        }
        this.cdRef.markForCheck();
    }

    registerOnChange(fn: (value: string) => void): void {
        this.propagateChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.propagateTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        if (isDisabled) {
            this.articleCodeControl.disable({ emitEvent: false });
        } else {
            this.articleCodeControl.enable({ emitEvent: false });
        }
        this.cdRef.markForCheck();
    }

    onBlur(): void {
        this.propagateTouched();
    }

    validate(control: AbstractControl): ValidationErrors | null {
        this.parentControl = control;
        if (this.articleCodeControl.errors) {
            return this.articleCodeControl.errors;
        }
        return null;
    }

    registerOnValidatorChange(fn: () => void): void {
        this.propagateValidationChange = fn;
    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.complete();
    }
}
